import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Badge from "../wrappers/Badge";
import Box from "../wrappers/Box";
import Chip from "../wrappers/Chip";
import Stack from "../wrappers/Stack";
import Collapse from "../wrappers/Collapse";
import Typography from "../wrappers/Typography";
import useMediaQuery from "../wrappers/useMediaQuery";
import { Divider } from "@mui/material";
import { useSearchFiltersSections } from "./SearchFiltersSectionsContext";

/**
 * Agrupa filtros em uma seção expansível controlada ou local.
 *
 * @param {object} pProps Propriedades do componente.
 * @param {string} pProps.id Identificador estável da seção.
 * @param {string} pProps.title Título do gatilho e da região.
 * @param {React.ReactNode} pProps.children Conteúdo preservado ao recolher.
 * @param {boolean} [pProps.defaultOpen=false] Abertura inicial no modo local.
 * @param {boolean} [pProps.open] Abertura controlada pelo consumidor.
 * @param {Function} [pProps.onOpenChange] Recebe a abertura solicitada.
 * @param {number} [pProps.activeFiltersCount=0] Quantidade já calculada de filtros ativos.
 * @returns {React.ReactElement}
 */
const SearchFiltersSection = (pProps) => {
  const {
    id,
    title,
    children,
    defaultOpen = false,
    open,
    onOpenChange,
    activeFiltersCount = 0,
  } = pProps;
  const [xLocalOpen, setLocalOpen] = useState(defaultOpen);
  const xControlled = open !== undefined;
  const xSectionsContext = useSearchFiltersSections();
  const xContextOpen = xSectionsContext?.sections[id]?.open;
  const xOpen = xControlled
    ? open
    : xContextOpen === undefined
    ? xLocalOpen
    : xContextOpen;
  const xContentId = `${id}-content`;
  const xPrefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );
  const xHasActiveFilters = activeFiltersCount > 0;
  const xAriaLabel = xHasActiveFilters
    ? `${title}, ${activeFiltersCount} filtros ativos`
    : title;

  /** Registra a seção no shell para permitir a coordenação coletiva. */
  useEffect(() => {
    if (!xSectionsContext) {
      return undefined;
    }

    return xSectionsContext.registerSection(id, defaultOpen);
  }, [defaultOpen, id, xSectionsContext?.registerSection]);

  /** Mantém o coordenador alinhado ao estado e ao callback controlados. */
  useEffect(() => {
    if (!xSectionsContext) {
      return;
    }

    xSectionsContext.syncSection(id, xControlled, open, onOpenChange);
  }, [id, onOpenChange, open, xControlled, xSectionsContext?.syncSection]);

  /** Alterna o estado local ou solicita a alteração ao consumidor. */
  const pvHandleToggle = () => {
    const xNextOpen = !xOpen;

    if (xSectionsContext) {
      xSectionsContext.setSectionOpen(id, xNextOpen);
    } else {
      if (!xControlled) {
        setLocalOpen(xNextOpen);
      }

      onOpenChange?.(xNextOpen);
    }
  };

  return (
    <Box mb={2}>
      <Box
        aria-controls={xContentId}
        aria-expanded={xOpen}
        aria-label={xAriaLabel}
        component="button"
        onClick={pvHandleToggle}
        sx={{
          alignItems: "center",
          background: "transparent",
          border: 0,
          color: "text.primary",
          cursor: "pointer",
          display: "flex",
          font: "inherit",
          justifyContent: "space-between",
          minHeight: 44,
          p: 0,
          py: 1,
          textAlign: "left",
          width: "100%",
          "&:focus-visible": {
            outline: "3px solid",
            outlineColor: "primary.light",
            outlineOffset: 2,
          },
        }}
        type="button"
      >
        <Stack direction="row" gap={1} alignItems="center">
          <Typography component="span" variant="overline" fontWeight={600}>
            {title}
          </Typography>
          <Stack sx={{ width: "24px" }}>
            {xHasActiveFilters && (
              <Chip label={activeFiltersCount} color="primary" size="small" />
            )}
          </Stack>
        </Stack>
        <Typography
          aria-hidden="true"
          variant="body1"
          component="span"
          fontWeight={600}
          color="primary"
        >
          {xOpen ? "−" : "+"}
        </Typography>
      </Box>
      <Divider />
      <Collapse
        in={xOpen}
        timeout={xPrefersReducedMotion ? 0 : "auto"}
        sx={{
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        }}
      >
        <Stack
          aria-label={title}
          id={xContentId}
          role="region"
          sx={{ pt: 1, gap: 1 }}
        >
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
};

SearchFiltersSection.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  defaultOpen: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  activeFiltersCount: PropTypes.number,
};

export default SearchFiltersSection;
